import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useIdeaDetailViewModel } from "../viewmodel/useIdeaDetailViewModel";
import { useIdeaActionsViewModel } from "../viewmodel/useIdeaActionsViewModel";
import Loader from "@/core/components/loader/Loader";
import { IdeaHeader } from "../components/IdeaHeader";
import { DiscussionTab } from "../components/tabs/DiscussionTab";
import { PartnershipTab } from "../components/tabs/PartnershipTab/PartnershipTab";
import { ConfirmationModals } from "../components/modals/ConfirmationModals";
import { SEO } from "@/core/components/SEO/SEO";
import { SvgIcon } from "@/core/components/svgIcon/SvgIcon";
import "./IdeaDetailPage.scss";
import { IdeaSidebar } from "../components/IdeaSidebar";

export const IdeaDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"discussion" | "partnership">(
    "discussion",
  );

  const {
    selectedIdea,
    loading,
    error,
    user,
    userBasicInfo,
    isIdeaInRequests,
    isIdeaInInvitations,
    refetchIdea,
  } = useIdeaDetailViewModel(id);

  const ideaActions = useIdeaActionsViewModel(id, user?.uid?.toString());

  // Combine actions from both view models into a single prop object
  const combinedIdeaActions = {
    ...ideaActions,
    refetchIdea,
  };

  if (loading) {
    return (
      <div className="idea-detail-page">
        <Loader overlay />
      </div>
    );
  }

  if (error) {
    return (
      <div className="idea-detail-page">
        <div className="error-container">
          <h2>Error loading idea</h2>
          <p>{error}</p>
          <button onClick={() => navigate("/ideas")} className="btn-primary">
            Back to Ideas
          </button>
        </div>
      </div>
    );
  }

  if (!selectedIdea) {
    return <div className="idea-detail-page"></div>;
  }

  const showDiscussionAndPartnership =
    selectedIdea.is_member && selectedIdea.state.toLowerCase() === "approved";

  const showOwnerUnpublishedMessage =
    selectedIdea.is_owner &&
    (selectedIdea.state.toLowerCase() === "draft" ||
      selectedIdea.state.toLowerCase() === "pending" ||
      selectedIdea.state.toLowerCase() === "declined");

  return (
    <>
      <div className="idea-detail-page">
        <SEO
          title={`${selectedIdea?.title || "Ideas"} | EUcalls`}
          description={`${selectedIdea?.title || "Ideas"}`}
        />

        <div className="page-header">
          <button className="back-button" onClick={() => navigate(-1)}>
            <SvgIcon icon="icon-arrow-left" />
            <span>Back</span>
          </button>
        </div>

        <div className="idea-content">
          <div className="content-main">
            <IdeaHeader idea={selectedIdea} />
          </div>

          <div className="content-sidebar">
            <IdeaSidebar
              idea={selectedIdea}
              user={user}
              userBasicInfo={userBasicInfo}
              isIdeaInRequests={isIdeaInRequests()}
              isIdeaInInvitations={isIdeaInInvitations()}
              ideaActions={combinedIdeaActions}
            />
          </div>
        </div>

        {!selectedIdea.is_member ? (
          <div className="tabs-section forMembers">
            <div className="idea-partnership">Discussion & Partnership</div>
            <div className="forMembers-body">
              <div className="forMembers-icon">
                <SvgIcon icon="icon-forMembers" />
              </div>
              <div className="forMembers-title">
                Discussion & Partnership is only visible to idea members
              </div>
              <div className="forMembers-text">
                Join this idea to unlock full access.
              </div>
            </div>
          </div>
        ) : showOwnerUnpublishedMessage ? (
          <div className="tabs-section forMembers">
            <div className="idea-partnership">Discussion & Partnership</div>
            <div className="forMembers-body">
              <div className="forMembers-icon">
                <SvgIcon icon="icon-forMembers" />
              </div>
              <div className="forMembers-title">
                Discussion & Partnership are enabled once the idea is published
              </div>
              <div className="forMembers-text">
                Publish your idea and start collaborating..
              </div>
            </div>
          </div>
        ) : (
          showDiscussionAndPartnership && (
            <div className="tabs-section">
              <div className="idea-partnership">Partnership</div>
              <div className="tabs-header">
                <button
                  className={`tab-button ${
                    activeTab === "discussion" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("discussion")}
                >
                  Discussion
                </button>
                <button
                  className={`tab-button ${
                    activeTab === "partnership" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("partnership")}
                >
                  Partnership (
                  {selectedIdea.partnership !== null &&
                    (Array.isArray(selectedIdea.partnership)
                      ? selectedIdea.partnership.length
                      : Object.keys(selectedIdea.partnership).length)}
                  )
                </button>
              </div>

              <div className="tabs-content">
                {activeTab === "discussion" && (
                  <DiscussionTab ideaId={id} ideaState={selectedIdea.state} />
                )}
                {activeTab === "partnership" && (
                  <PartnershipTab
                    ideaId={id}
                    idea={selectedIdea}
                    ideaActions={combinedIdeaActions}
                  />
                )}
              </div>
            </div>
          )
        )}
      </div>

      {/* Only pass these 4 props - no modal state props */}
      <ConfirmationModals
        ideaId={id}
        selectedIdea={selectedIdea}
        user={user}
        ideaActions={combinedIdeaActions}
      />
    </>
  );
};
